import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useLocation from '../../../../store/ReactQueries/locations/useLocation';
import useAddLocation from '../../../../store/ReactQueries/locations/useAddLocations';
import useUpdateLocation from '../../../../store/ReactQueries/locations/useUpdateLocation';
import useDeleteLocations from '../../../../store/ReactQueries/locations/useDeleteLocations';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { LocationInterface } from '../../../../models/TicketModule/Location';
import { locationSchema } from '../../../../domain/ValidationSchemas/Ticket/location';

export const Locations = () => {

    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<LocationInterface>({
        id: '',
        title: '',
        isDefault: false,
        companyId: '',
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: locations, isLoading, error: locationError, refetch } = useLocation(page, pageSize);

    const addLocation = useAddLocation(page, pageSize);
    const updateLocation = useUpdateLocation(page, pageSize);
    const deleteLocation = useDeleteLocations(page, pageSize);


    useEffect(() => {
        if (locationError) ToastMessage.Failed(`Error: ${locationError.message}`);
    }, [locationError]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 200 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        {
            field: 'company',
            headerName: 'Company',
            type: "string",
            width: 150,
            editable: false,
            renderCell: ({ row }) => <span>{row.company ? row.company.title : ''}</span>,
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'companyId', headerName: 'CompanyId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false, companyId: '' });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(locationSchema, form);

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: LocationInterface = {
            id: form.id,
            title: form.title,
            isDefault: form.isDefault,
            companyId: form.companyId
        }
        if (upForm.id === '') {
            const { id, ...formData } = form;
            addLocation.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            });
        } else {
            updateLocation.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error) => handleError(error),
            })
        }

        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteLocation.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error) => handleError(error),
            });
        }
    };

    return (
        <CustomDataGrid
            name={language['Locations']}
            rows={locations || []}
            showColumnModal={['title', 'isDefault', 'companyId']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById', 'companyId']}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setPage={setPage}
            setForm={setForm}
            addItems={addItems}
            setModal={setModal}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
        />
    );
}
export default Locations;